import { debounceTime, map, tap } from 'rxjs/operators'
import {
    BehaviorSubject,
    fromEvent,
    Observable,
    startWith,
    Subscription,
    switchMap,
} from 'rxjs'
import { TranslateService } from '@ngx-translate/core'
import { InstantSearchStatus } from 'instantsearch.js/es/lib/InstantSearch'
import { BaseHit } from 'instantsearch.js'
import connectHits, {
    HitsConnectorParams as CParams,
    HitsWidgetDescription as WDescription,
} from 'instantsearch.js/es/connectors/hits/connectHits'

import {
    Component,
    EventEmitter,
    forwardRef,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    TrackByFunction,
} from '@angular/core'
import { ActivatedRoute } from '@angular/router'

import { Product } from '@app-domains/content-blocks/components/product-card/product-card.types'
import { GridType, GridTypes } from '@app-types/grid.types'
import { ProductAlgoliaHit } from '@app-types/algolia.types'
import { EmptyState } from '@app-domains/empty-state/types/empty-state'

import { TranslationKey } from '@app-pipes/typed-translate/typed-translate.pipe'
import { AnalyticsService } from '@app-domains/analytics/analytics.service'
import { AbstractInstantsearchWidgetComponent } from '@app-domains/algolia/abstract-instantsearch-widget.component'
import { AlgoliaInstantsearchComponent } from '@app-domains/algolia/components/algolia-instantsearch.component'
import {
    SearchService,
    ProductEnhanceService,
    ProductSelectionService,
    ProductService,
} from '@app-services'

@Component({
    selector: 'app-product-overview-hits',
    templateUrl: './product-overview-hits.component.html',
    styleUrls: ['./product-overview-hits.component.scss'],
})
export class ProductOverviewHitsComponent extends AbstractInstantsearchWidgetComponent<
WDescription,
CParams
> implements OnInit, OnDestroy {

    public override readonly widgetName = 'Hits'

    @Input()
    public gridType: GridType[] = [
            {
                name: 'large-normal',
                cells: 5,
            },
            {
                name: 'normal',
                cells: 6,
            },
            {
                name: 'large-reversed',
                cells: 5,
            },
            {
                name: 'normal',
                cells: 6,
            },
            {
                name: 'xlarge-reversed',
                cells: 3,
            },
            {
                name: 'normal',
                cells: 6,
            },
        ]

    @Input()
    public emptyStateObject: EmptyState

    @Output()
    public productsLoaded = new EventEmitter<Product[]>()

    public status$: Observable<InstantSearchStatus>

    public subscriptions$ = new Subscription()

    private hitSubject = new BehaviorSubject<BaseHit[]>([])
    public products$: Observable<Product[]> = this.hitSubject.pipe(
        debounceTime(100),
        map((hits) => hits.map((hit) => this.productService.transformHitToProduct(hit as ProductAlgoliaHit))),
        tap((products) => this.productService.inWishlist(products)),
        tap((products) => this.productEnhanceService.initialize(products)),
        tap((products) => this.productsLoaded.emit(products)),
    )

    public maxSelectedProductsCount: number = 500

    public override updateState = (state: WDescription['renderState']) => {
        this.state = state
        this.hitSubject.next(state.hits)
        this.searchService.productResultsCount.next(state.results?.nbHits ?? 0)
    }

    constructor(
        @Inject(forwardRef(() => AlgoliaInstantsearchComponent))
        public instantSearchInstance: AlgoliaInstantsearchComponent,
        public productSelectionService: ProductSelectionService,
        private translateService: TranslateService,
        private route: ActivatedRoute,
        private analytics: AnalyticsService,
        private productService: ProductService,
        private searchService: SearchService,
        private productEnhanceService: ProductEnhanceService,
    ) {
        super()
    }

    public trackFn: TrackByFunction<{ type: GridTypes; hits: Product[] }> = (i, hit) => hit.type

    public override ngOnInit(): void {
        this.createWidget(connectHits, {})
        super.ngOnInit()

        this.route.params.subscribe(() => this.productSelectionService.deselectAllProducts())

        this.status$ = this.route.paramMap.pipe(
            switchMap(() =>
                fromEvent(this.instantSearchInstance.instantSearchInstance, 'render').pipe(
                    debounceTime(250),
                    startWith('loading'),
                    map(() => this.instantSearchInstance.instantSearchInstance.status),
                ),
            ),
            startWith('loading' as InstantSearchStatus),
        )

        this.subscriptions$.add(
            this.route.queryParams.subscribe(params => {
                const filterLabelKeys: TranslationKey[] = [
                    'catalog-index.refinement-lists.brands',
                    'catalog-index.refinement-lists.category',
                    'catalog-index.refinement-lists.color',
                    'catalog-index.refinement-lists.material',
                    'catalog-index.refinement-lists.status',
                    'products.algolia.delivery-time.delivery-time-in-weeks',
                ]

                const filterNames = filterLabelKeys.map(key => this.translateService.instant(key))

                Object.entries(params).forEach(([key, value]) => {
                    if (filterNames.includes(key)) {
                        this.analytics.logFilterOn('checkbox', key, value)
                    }
                })
            }),
        )
    }

    public selectAllProducts(): void {
        const instantSearchHelper = this.instantSearchInstance.instantSearchInstance.helper

        if (! instantSearchHelper) {
            return
        }

        const searchQuery = instantSearchHelper.getQuery()

        void this.productSelectionService.selectAllMatchingAlgoliaQuery(searchQuery, this.maxSelectedProductsCount)
    }

    public override ngOnDestroy(): void {
        this.subscriptions$.unsubscribe()
    }

}
